import React, { useEffect, useRef } from "react";
import JsBarcode from "jsbarcode";

// ─── API SHAPE (QuotationSalesController) ────────────────────────────────────
//
// Header fields:
//   quoteNumber, date, validityDate, customerName, shippingAddress,
//   locationName, remarks, status, totalAmount, shippingCost,
//   hasVat, hasEwt, vatAmount, ewtAmount, vatableAmount
//
// quotationSalesProducts[]:
//   productId, productName, itemCode, quantity, unitPrice, lineTotal,
//   uomName, discountType, discountValue, discountAmount
//
// ─────────────────────────────────────────────────────────────────────────────

export const QuotationSalesPrint = React.forwardRef(({ data }, ref) => {
  const barcodeRef = useRef(null);
  const quoteNumber = data?.quoteNumber;

  useEffect(() => {
    if (quoteNumber && barcodeRef.current) {
      try {
        JsBarcode(barcodeRef.current, quoteNumber, {
          format: "CODE128",
          lineColor: "#000000",
          width: 1.5,
          height: 40,
          displayValue: false,
          margin: 0,
        });
      } catch (e) {
        console.error("Barcode error", e);
      }
    }
  }, [quoteNumber]);

  if (!data) return null;

  const items = data.quotationSalesProducts || [];
  const vatAmount = data.vatAmount || 0;
  const ewtAmount = data.ewtAmount || 0;
  const shippingCost = data.shippingCost || 0;

  // ── Compute totals ────────────────────────────────────────────────────────
  // The lineTotal from the API already has the discount subtracted.
  const subtotal = items.reduce((s, i) => s + (i.lineTotal || 0), 0);
  const totalDiscount = items.reduce((s, i) => s + (i.discountAmount || 0), 0);

  // Grand total computation
  const grandTotal =
    data.totalAmount || subtotal + shippingCost + vatAmount - ewtAmount;

  const totalQty = items.reduce((s, i) => s + (i.quantity || 0), 0);
  const hasDeductionsOrAdditions =
    totalDiscount > 0 || vatAmount > 0 || ewtAmount > 0 || shippingCost > 0;

  const fmt = (v) =>
    new Intl.NumberFormat("en-PH", {
      style: "currency",
      currency: "PHP",
    }).format(v || 0);

  return (
    <div
      ref={ref}
      className="p-8 bg-white text-black text-xs font-sans max-w-[800px] mx-auto"
    >
      {/* ── HEADER ── */}
      <div className="text-center mb-6">
        <h1 className="text-xl font-bold uppercase">Ichthus Technology</h1>
        <div className="text-[10px] text-gray-600">
          <p># 3128 Sto. Rosario Street, Barangay</p>
          <p>Mapulang Lupa, Valenzuela, 1440 Metro</p>
        </div>
        <div className="mt-2 flex justify-center">
          <svg ref={barcodeRef}></svg>
        </div>
        <p className="text-[9px] mt-1 tracking-widest">{quoteNumber}</p>
      </div>

      {/* ── INFO GRID ── */}
      <div className="flex justify-between items-start mb-6 gap-4">
        {/* Bill To */}
        <div className="w-1/2">
          <h2 className="font-bold text-sm uppercase mb-1 bg-gray-100 p-1">
            Quote To:
          </h2>
          <div className="pl-1 space-y-0.5">
            <p className="font-bold uppercase text-sm">
              {data.customerName || "WALK-IN CUSTOMER"}
            </p>
            <p className="uppercase w-3/4 text-gray-700">
              {data.shippingAddress || "No Address Provided"}
            </p>
          </div>
        </div>

        {/* QS Details */}
        <div className="w-1/2">
          <h2 className="font-bold text-lg mb-2 text-right">SALES QUOTATION</h2>
          <div className="grid grid-cols-2 gap-y-1 text-xs text-right">
            <span className="font-bold text-gray-600">Quote No.</span>
            <span className="font-semibold">{quoteNumber}</span>

            <span className="font-bold text-gray-600">Date</span>
            <span>
              {data.date ? new Date(data.date).toLocaleDateString() : "—"}
            </span>

            <span className="font-bold text-gray-600">Validity Date</span>
            <span>
              {data.validityDate
                ? new Date(data.validityDate).toLocaleDateString()
                : "—"}
            </span>

            <span className="font-bold text-gray-600">Branch / Location</span>
            <span>{data.locationName || "Main Office"}</span>
          </div>

          <div className="mt-4 bg-gray-100 p-2 border border-gray-200">
            <div className="flex justify-between font-bold">
              <span>Total Items</span>
              <span>{totalQty} units</span>
            </div>
          </div>
        </div>
      </div>

      {/* ── TABLE HEADER ── */}
      <div className="mt-4 border-b-2 border-black pb-1 mb-1 bg-gray-100 flex font-bold text-xs uppercase p-1">
        <div className="w-6">#</div>
        <div className="w-24">Item Code</div>
        <div className="flex-1">Description</div>
        <div className="w-24 text-center">Qty / UOM</div>
        <div className="w-24 text-right">Unit Price</div>
        <div className="w-24 text-right">Total</div>
      </div>

      {/* ── TABLE BODY ── */}
      {/* <div className="min-h-[300px] font-sans tracking-tight"> */}
      <div className="font-sans tracking-tight">
        <div className="divide-y divide-gray-100 border-b border-gray-100">
          {items.map((item, index) => {
            const qty = item.quantity || 0;
            const price = item.unitPrice || 0;
            const lineTotal = item.lineTotal || 0;
            const discountAmount = item.discountAmount || 0;

            return (
              <div
                key={item.id || index}
                className="flex items-start py-1.5 text-[10px] uppercase font-bold leading-snug"
              >
                <div className="w-6 text-center text-gray-300 pt-0.5">
                  {(index + 1).toString().padStart(2, "0")}
                </div>

                <div className="w-24 text-blue-500 truncate px-1 pt-0.5 font-mono">
                  {item.itemCode || "—"}
                </div>

                <div className="flex-1 px-1">
                  <div className="text-gray-800">{item.productName}</div>
                  {/* ← DISCOUNT INDICATOR per line */}
                  {discountAmount > 0 && (
                    <div className="text-[9px] text-rose-500 font-semibold normal-case mt-0.5 flex items-center gap-0.5">
                      Includes discount of {fmt(discountAmount)}
                    </div>
                  )}
                </div>

                <div className="w-24 flex justify-center items-start gap-0.5 pt-0.5">
                  <span className="text-gray-900">{qty}</span>
                  {item.uomName && (
                    <span className="text-gray-400 text-[9px] ml-0.5 normal-case">
                      {item.uomName}
                    </span>
                  )}
                </div>

                <div className="w-24 text-right text-gray-500 tabular-nums px-1 pt-0.5">
                  {fmt(price)}
                </div>

                <div className="w-24 text-right text-gray-900 tabular-nums pl-1 pr-2 pt-0.5">
                  {fmt(lineTotal)}
                </div>
              </div>
            );
          })}
        </div>

        {/* Remarks Section */}
        {data.remarks && (
          <div className="mt-4 p-2 bg-gray-50 text-[10px] text-gray-600 border border-gray-100 rounded">
            <span className="font-bold uppercase text-gray-800 block mb-0.5">
              Remarks / Notes:
            </span>
            {data.remarks}
          </div>
        )}
      </div>

      {/* ── TOTALS ── */}
      <div className="mt-4 border-t-2 border-black pt-2 flex justify-end">
        <div className="w-1/2 space-y-1">
          {hasDeductionsOrAdditions && (
            <div className="flex justify-between text-xs text-gray-600 px-2">
              <span>Subtotal:</span>
              <span>{fmt(subtotal)}</span>
            </div>
          )}

          {shippingCost > 0 && (
            <div className="flex justify-between text-xs text-gray-600 px-2">
              <span>Shipping Cost:</span>
              <span>{fmt(shippingCost)}</span>
            </div>
          )}

          {vatAmount > 0 && (
            <div className="flex justify-between text-xs text-gray-600 px-2">
              <span>VAT (12%):</span>
              <span>{fmt(vatAmount)}</span>
            </div>
          )}

          {ewtAmount > 0 && (
            <div className="flex justify-between text-xs text-gray-600 px-2">
              <span>EWT (1%):</span>
              <span>− {fmt(ewtAmount)}</span>
            </div>
          )}

          <div className="flex justify-between text-sm font-bold bg-gray-100 p-2 rounded">
            <span>Total Amount:</span>
            <span>{fmt(grandTotal)}</span>
          </div>
        </div>
      </div>

      <div className="mt-4">
        <p className="italic mb-2">
          Note: For special orders and other curing time, please feel free to inquire. We can customize everything for your needs.
        </p>
        
        <h3 className="font-bold mb-2">Terms and Conditions:</h3>
        
        <ol className="list-decimal list-inside space-y-1 mb-4">
          <li>{`Terms of payment is ${data?.termDescription || "-"}`}</li>
          <li>Prices quoted above are INCLUSIVE of the 12% Value Added Tax and subject to change without prior notice</li>
          <li>Jobsite Conditions are subject to ocular inspection prior to pouring</li>
          <li>Compression tests on cylinder samples for day of the design strength will be free of charge. Additional tests requested by the customer will be on the account of the customer.</li>
          <li>Price may change due to consistent fuel price increase.</li>
        </ol>

        <p className="font-semibold">
          We hope you find everything in order and we look forward to be of service to you soon
        </p>
        
        <p className="mt-2">
          For queries and other concerns, feel free to contact:
        </p>
      </div>

      {/* ── SIGNATURES ── */}
      <div className="mt-16 grid grid-cols-2 gap-12 text-[10px]">
        <div>
          <div className="border-b border-black mb-1"></div>
          <p className="font-bold">Prepared By</p>
        </div>
        <div>
          <div className="border-b border-black mb-1"></div>
          <p className="font-bold">Customer Conforme / Signature</p>
        </div>
      </div>

      <div className="mt-8 text-center text-[9px] text-gray-400">
        System Generated Sales Quotation ({quoteNumber}) | Printed on{" "}
        {new Date().toLocaleString()}
      </div>
    </div>
  );
});

export default QuotationSalesPrint;
